import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { mediaUrl } from '@/hooks/useMediaUrl';
import type { GroupWithMembership } from '@shared/types';

export function GroupsPage() {
  const [groups, setGroups] = useState<GroupWithMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', privacy: 'open' as 'open' | 'closed' | 'secret' });
  const [actionLoading, setActionLoading] = useState<Set<string>>(new Set());

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const q = searchQuery.trim() ? `?q=${encodeURIComponent(searchQuery.trim())}` : '';
      const data = await api.get<GroupWithMembership[]>(`/groups${q}`);
      setGroups(data);
    } catch {
      // ignore
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    const timer = setTimeout(fetchGroups, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setCreating(true);
    try {
      const group = await api.post<GroupWithMembership>('/groups', {
        name: form.name.trim(),
        description: form.description.trim(),
        privacy: form.privacy,
      });
      setGroups([{ ...group, my_role: 'admin' }, ...groups]);
      setForm({ name: '', description: '', privacy: 'open' });
      setShowCreate(false);
    } catch {
      // ignore
    }
    setCreating(false);
  };

  const handleJoinLeave = async (group: GroupWithMembership) => {
    setActionLoading((prev) => new Set(prev).add(group.id));
    try {
      if (group.my_role) {
        await api.del(`/groups/${group.id}/leave`);
        setGroups(groups.map((g) => g.id === group.id ? { ...g, my_role: null, member_count: g.member_count - 1 } : g));
      } else {
        await api.post(`/groups/${group.id}/join`);
        setGroups(groups.map((g) => g.id === group.id ? { ...g, my_role: 'member', member_count: g.member_count + 1 } : g));
      }
    } catch {
      // ignore
    }
    setActionLoading((prev) => {
      const next = new Set(prev);
      next.delete(group.id);
      return next;
    });
  };

  const privacyBadge = (privacy: string) => {
    switch (privacy) {
      case 'open': return <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-bold">Open</span>;
      case 'closed': return <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold">Closed</span>;
      case 'secret': return <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-bold">Secret</span>;
      default: return null;
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-3 py-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-bold text-sand-800">&#127968;&#65039; Groups</h1>
        <button onClick={() => setShowCreate(!showCreate)} className="btn-primary text-xs py-1.5 px-4">
          + Create Group
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="card mb-4 p-4 space-y-3">
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Group name"
            className="input w-full text-xs"
          />
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Description"
            className="input w-full text-xs resize-none"
            rows={2}
          />
          <div>
            <label className="block text-[10px] font-bold text-sand-500 uppercase mb-1">Privacy</label>
            <select value={form.privacy} onChange={(e) => setForm({ ...form, privacy: e.target.value as any })} className="input text-xs">
              <option value="open">Open</option>
              <option value="closed">Closed</option>
              <option value="secret">Secret</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={creating || !form.name.trim()} className="btn-primary text-xs py-1.5 px-4 disabled:opacity-40">
              {creating ? 'Creating...' : 'Create'}
            </button>
            <button onClick={() => setShowCreate(false)} className="btn-secondary text-xs py-1.5 px-4">Cancel</button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="mb-4">
        <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search groups..."
          className="input w-full text-xs"
        />
      </div>

      {/* Group list */}
      {loading ? (
        <p className="text-center text-sand-500 text-xs py-8">Loading groups...</p>
      ) : groups.length === 0 ? (
        <div className="text-center py-8 text-sand-500">
          <div className="text-3xl mb-2">&#127968;&#65039;</div>
          <p className="text-sm">No groups found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((group) => (
            <div key={group.id} className="card p-4 flex items-start gap-3">
              <div className="w-14 h-14 rounded bg-sand-200 flex items-center justify-center text-2xl shrink-0">
                {group.image_key ? (
                  <img src={mediaUrl(group.image_key)} alt="" className="w-full h-full object-cover rounded" />
                ) : (
                  '&#127968;'
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Link to={`/groups/${group.id}`} className="link font-bold text-sm truncate">
                    {group.name}
                  </Link>
                  {privacyBadge(group.privacy)}
                </div>
                {group.description && (
                  <p className="text-xs text-sand-600 mb-1 line-clamp-2">{group.description}</p>
                )}
                <div className="text-[10px] text-sand-500">
                  {group.member_count} member{group.member_count !== 1 ? 's' : ''}
                </div>
              </div>
              <button
                onClick={() => handleJoinLeave(group)}
                disabled={actionLoading.has(group.id)}
                className={`text-xs font-bold py-1.5 px-4 rounded-md transition-colors shrink-0 disabled:opacity-40 ${
                  group.my_role
                    ? 'bg-sand-200 text-sand-600 hover:bg-red-100 hover:text-red-600'
                    : 'btn-primary'
                }`}
              >
                {group.my_role ? 'Leave' : 'Join'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
